import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, Sparkles } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";
import { GamifiedButton } from "@/components/GamifiedButton";
import { Logo } from "@/components/Logo";
import { ErrorMessage } from "@/components/Feedback";
import { useSeo } from "@/hooks/useSeo";

export default function Login() {
  useSeo("Connexion", "Connecte-toi à Quran Coach pour apprendre à lire et mémoriser le Coran, suivre ta progression et réviser au bon moment.");
  const { login, demoLogin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try { await login(email, password); navigate("/accueil"); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const demo = async () => {
    setError(""); setDemoLoading(true);
    try { await demoLogin(); navigate("/accueil"); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setDemoLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-gradient-to-br from-primary to-secondary text-white relative overflow-hidden">
        <div className="absolute inset-0 islamic-star-pattern opacity-40" />
        <Logo className="relative [&_div]:text-white" withText={false} size="lg" />
        <div className="relative">
          <h1 className="text-4xl font-black leading-tight mb-4">Ton compagnon intelligent pour mémoriser le Coran.</h1>
          <p className="text-white/80 text-lg">Apprends, écoute, récite et révise à ton rythme — avec bienveillance.</p>
        </div>
        <p className="relative text-white/60 text-sm">Coach Quran · Ton compagnon du Coran</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 islamic-bg">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex justify-center"><Logo size="lg" /></div>
          <h2 className="text-3xl font-black text-slate-800 mb-1">Bon retour 👋</h2>
          <p className="text-slate-500 mb-6">Connecte-toi pour continuer ta progression.</p>

          {error && <div className="mb-4"><ErrorMessage message={error} /></div>}

          <form onSubmit={submit} className="space-y-4">
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input data-testid="login-email" type="email" required placeholder="Adresse email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-12 pr-4 py-3.5 rounded-2xl border-2 border-slate-200 focus:border-primary outline-none font-semibold" />
            </div>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input data-testid="login-password" type="password" required placeholder="Mot de passe" value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-12 pr-4 py-3.5 rounded-2xl border-2 border-slate-200 focus:border-primary outline-none font-semibold" />
            </div>
            <div className="text-right">
              <Link to="/mot-de-passe-oublie" className="text-sm font-bold text-primary hover:underline">Mot de passe oublié ?</Link>
            </div>
            <GamifiedButton type="submit" disabled={loading} className="w-full" data-testid="login-submit">
              {loading ? "Connexion…" : "Se connecter"}
            </GamifiedButton>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs font-bold text-slate-400 uppercase">ou</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <GamifiedButton variant="accent" onClick={demo} disabled={demoLoading} className="w-full" data-testid="demo-login-btn">
            <Sparkles className="w-5 h-5" /> {demoLoading ? "Chargement…" : "Essayer le mode démo (Ahmed)"}
          </GamifiedButton>

          <p className="text-center text-sm text-slate-500 mt-6">
            Pas encore de compte ?{" "}
            <Link to="/register" data-testid="go-register" className="font-extrabold text-primary hover:underline">Créer un compte</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
